import * as React from "react";

/**
 * The class list a portalled surface should carry so its design tokens resolve in the scope it
 * visually belongs to.
 *
 * ── WHAT THIS IS, AND WHAT IT IS NOT ──────────────────────────────────────────────────
 *
 * Density and theme are delivered by CLASSES — `salt-theme salt-density-low` — and every
 * spacing, size and colour token is defined against them. A portal appends to `document.body`,
 * so it only inherits what an ANCESTOR of body provides.
 *
 * This was written believing it fixed a shipped bug. It did not, and the measurement is worth
 * recording because it says something surprising about the vendor.
 *
 * MEASURED in this app, whose provider puts `salt-theme salt-density-low` on <html> itself:
 *
 *                                   --salt-spacing-100    panel padding
 *   the page                              12px
 *   our portalled panel                   12px               12px      <- matches the page
 *   the VENDOR's portalled panel           8px                8px      <- does not
 *
 * The vendor re-mounts a `SaltProvider` inside its portal, and that provider takes its DEFAULT
 * density — `medium` — rather than the page's. So every portalled Salt surface in this app has
 * been rendering one density step tighter than everything around it. Our version inherits from
 * <html> and is simply correct; the A/B difference here is the vendor's defect, not ours.
 *
 * ── SO WHY KEEP THIS AT ALL ───────────────────────────────────────────────────────────
 *
 * Because inheriting from <html> is only correct while the scope is global. The moment a page
 * nests a provider — a dark panel inside a light page, a compact region inside a comfortable
 * one — a popover opened from inside that region belongs to the region, and a portal would
 * silently take the page's scope instead.
 *
 * Reading the class list from the TRIGGER's nearest provider is what expresses that: the
 * surface adopts the scope of the thing that opened it. In this app there is no
 * `.salt-provider` element at all, so it resolves to "" and the wrapper is inert — which is
 * the correct answer here, not an accident.
 */
const PROVIDER_SELECTOR = ".salt-provider";

export function usePortalScopeClassName(reference: Element | null | undefined): string {
  const [scope, setScope] = React.useState("");

  React.useLayoutEffect(() => {
    if (!reference) {
      setScope("");
      return;
    }
    const provider = reference.closest(PROVIDER_SELECTOR);
    // No provider is the normal case in this app, not an error: the scope lives on <html> and
    // the portal inherits it already.
    setScope(provider instanceof HTMLElement ? provider.className : "");
  }, [reference]);

  return scope;
}
